/** Source: https://developer.paddle.com/concepts/sell/supported-currencies/ (2026-09-10). */
export const PADDLE_CURRENCY_REGISTRY_VERSION = 'paddle-currencies-2026-09-10-v1';
export const PADDLE_CURRENCY_EXPONENTS: Readonly<Record<string, 0 | 2>> = Object.freeze({
  USD: 2, GBP: 2, EUR: 2, ARS: 2, AUD: 2, BRL: 2, CAD: 2, CHF: 2,
  CLP: 0, CNY: 2, COP: 2, CZK: 2, DKK: 2, HKD: 2, HUF: 2, ILS: 2,
  INR: 2, JPY: 0, KRW: 0, MXN: 2, NOK: 2, NZD: 2, PEN: 2, PLN: 2,
  RUB: 2, SEK: 2, SGD: 2, THB: 2, TRY: 2, TWD: 2, UAH: 2, VND: 0, ZAR: 2,
});

export function paddleCurrencyExponent(currency: unknown): 0 | 2 | null {
  if (typeof currency !== 'string' || !/^[A-Za-z]{3}$/.test(currency)) return null;
  return PADDLE_CURRENCY_EXPONENTS[currency.toUpperCase()] ?? null;
}

/** Prices and payable checkout totals are nonnegative, exact minor units (AC-137).
 * Signed financial facts must opt into parsePaddleSignedApiMinor explicitly.
 */
export function parsePaddleApiMinor(value: unknown): number | null {
  const amount = parsePaddleSignedApiMinor(value);
  return amount === null || amount < 0 ? null : amount === 0 ? 0 : amount;
}

/** Exact signed minor units for financial facts, including adjustment amounts/fees.
 * Do not use this parser to accept a catalog price or payable checkout total.
 */
export function parsePaddleSignedApiMinor(value: unknown): number | null {
  if (typeof value === 'number') return Number.isSafeInteger(value) ? value : null;
  if (typeof value !== 'string' || !/^-?\d+$/.test(value)) return null;
  const amount = Number(value);
  return Number.isSafeInteger(amount) ? amount : null;
}

/** Converts the decimal representation, without rounding fractional minor units away. */
export function paddleCheckoutMajorToMinor(value: unknown, currency: string): number | null {
  const exponent = paddleCurrencyExponent(currency);
  if (exponent === null || typeof value !== 'number' || !Number.isFinite(value)) return null;
  const match = /^(-?)(\d+)(?:\.(\d+))?(?:e([+-]?\d+))?$/.exec(value.toString());
  if (!match) return null;
  const fraction = match[3] ?? '';
  let integer = BigInt(match[2] + fraction);
  const scale = Number(match[4] ?? 0) - fraction.length + exponent;
  if (scale >= 0) integer *= 10n ** BigInt(scale);
  else {
    const divisor = 10n ** BigInt(-scale);
    if (integer % divisor !== 0n) return null;
    integer /= divisor;
  }
  if (integer > BigInt(Number.MAX_SAFE_INTEGER)) return null;
  return Number(match[1] ? -integer : integer);
}

export function paddleMinorToMajor(value: number, currency: string): number | null {
  const exponent = paddleCurrencyExponent(currency);
  if (exponent === null || !Number.isSafeInteger(value)) return null;
  const major = value / 10 ** exponent;
  return paddleCheckoutMajorToMinor(major, currency) === value ? major : null;
}
